import { ulid } from 'ulid';
import { z } from 'zod';
/**
 * Ids are opaque, stable forever, and comfortably under the agreement's 128
 * characters (C6). ULIDs rather than sequence integers, specifically so that a
 * data migration cannot renumber a `resource_id` the partner has stored —
 * availability is exclusive per resource, so a reused resource id would silently
 * merge two rooms' calendars.
 */
export const ID_PREFIX = {
    appointment: 'ap',
    client: 'cl',
    service: 'sv',
    resource: 'rs',
    hold: 'hd',
    event: 'ev',
    apiKey: 'ak',
    webhookEndpoint: 'we',
    delivery: 'wd',
    certificate: 'ct',
    product: 'pr',
    staff: 'st',
    /** A line on an appointment, a settlement or an order (§3.6). */
    line: 'ln',
    /**
     * An order (spec §3.6) — a *promise*, as distinct from a settlement (`se_`),
     * which is money already recorded. The two are different objects with
     * different lifecycles and §6.5 names the distinction; giving them one prefix
     * would be the first step toward giving them one table.
     */
    order: 'or',
    /** One chosen modifier on one order line — the snapshot, not the menu row. */
    orderLineModifier: 'om',
    /** A preference, allergy or note on a client. */
    preference: 'pf',
    /** A closure on the calendar. */
    blackout: 'bo',
    /** A checkout document. */
    settlement: 'se',
    /**
     * **A refund** — a document, never an edit of the settlement it names
     * (`docs/money-second-pass-design.md` §4).
     *
     * Its own prefix for `order`'s reason: a refund has its own date of money
     * (§7.4 rule 1 — it lands on the day it was given back, not on the day of the
     * sale), its own tenders and its own lines, and giving it a `se_` would be the
     * first step toward giving it the settlements table and a sign.
     */
    refund: 'rf',
    /** A row in the operator audit trail. */
    audit: 'au',
    /**
     * **One way a sellable is sold** — a length, a size
     * (`item_options`, `docs/catalog-options-design.md`).
     *
     * `io_` and not `md_`: a modifier is a property of a *line* ("medium rare"),
     * an option is a property of the **sellable** itself, and the two mechanisms
     * deliberately never meet (design §2c). An id read aloud in a support call
     * should say which of the two it is — and the catalog's headline
     * `price_cents` and `duration_min` come from an `io_`, while no `md_` has
     * ever decided either.
     *
     * There is no prefix for an add-on **link**, because a link has no id: its
     * key is `(tenant, parent, add_on)` and the add-on itself is an ordinary
     * item with an ordinary item id — which is the whole reason an add-on is an
     * item rather than a choice inside a group (a thing the business sells has
     * one id, and a report can sum it).
     */
    itemOption: 'io',
    /** A choice group on a menu item — "Temperature", "Choose two sides". */
    modifierGroup: 'mg',
    /** One choice inside a group — "Medium rare", "+ avocado". */
    modifier: 'md',
    /**
     * A pay link (spec §3.7). It is the `custom_id` we set on the PayPal order,
     * which is what comes back on the capture webhook — the spike's proof 1 — so
     * it is the join between their world and ours and must be unguessable: a
     * sequence integer here would let anyone enumerate a tenant's outstanding
     * deposits by walking a URL.
     */
    paymentLink: 'pl',
    /**
     * One materialized sailing (spec §4.8) — "the 9:00 snorkel trip *exists* as a
     * scheduled thing… cancellable as a thing, with a manifest". It gets an id of
     * its own rather than being addressed by `(item, instant)` because the
     * operator action the vertical is built around — cancel *this* departure, and
     * cascade to its manifest — needs something to name, and a composite key
     * spelled into a URL is a key somebody eventually rounds to the minute.
     */
    departureInstance: 'di',
    /**
     * One span of externally-held time on a unit (spec §4.7, migration 0021) —
     * the row an OTA calendar import materializes.
     *
     * It gets an id because an import has to be able to *replace* what it wrote
     * last time: the natural key is `(calendar_ref, external_uid)` and the row is
     * addressed by that, but a re-import that deletes and re-inserts still needs
     * something to name each row it touched in an operator's report.
     */
    externalBusy: 'xb',
    /**
     * One photograph of one sellable thing (docs/guest-pages-design.md decision 2,
     * migration 0024) — a row of `item_media`.
     *
     * It gets an id rather than being addressed by `(item_id, position)` for the
     * reason `external_busy` gets one: position is a *rendering* fact that a
     * reorder rewrites, and an operator's report ("this URL 404s") has to be able
     * to name the row rather than the slot it happened to be in this morning.
     */
    itemMedia: 'im',
    /**
     * A photo album (docs/tour-photos-design.md decision 2, migration 0025) — the
     * thing a code opens.
     *
     * `al_` rather than `pa_` because the word the studio, the staff screen, the
     * guest page and the operator's support call all use is *album*; an id prefix
     * is read aloud in exactly those conversations.
     */
    photoAlbum: 'al',
    /**
     * One photograph in one album. `pm_`, not `im_`: `item_media` is the public
     * catalog and this is a family's faces, and the two must never be confusable
     * in a log line, a support ticket or a storage path — which is precisely what
     * a shared prefix would make them. The storage layout is
     * `{tenant}/{album}/{media}` and the last segment is this id, so the prefix is
     * also literally part of the object's address.
     */
    photoAlbumMedia: 'pm',
    /**
     * One orderable variant of a synced merch product (`item_fulfillment_variants`,
     * migration 0026) — "Classic Tee, Heather Grey, L".
     *
     * It gets an id of its own rather than being addressed by the provider's
     * `sync_variant_id`, and that is the load-bearing choice in the whole sync:
     * **a cart line quotes our id, never theirs.** A basket keyed on a
     * manufacturer's identifier would be a basket whose meaning is decided by a
     * third party's database — one re-sync that renumbered a variant and every
     * open cart would be pricing something else. Ours is stable because we minted
     * it, and the provider's id is a *column* on the row it maps to, which is
     * what a foreign key to somebody else's world should always be.
     */
    fulfillmentVariant: 'fv',
    /**
     * One newsletter subscription (docs/newsletter-design.md, migration 0032) —
     * a fact about an *address*, not about a guest.
     *
     * `sub_` and not two letters, which makes it the one prefix in this table
     * that is three characters long. The design names it that way on the page
     * ("id — prefixed ULID (sub_…)") and it is worth keeping: every other id
     * here belongs to something a person or a business owns, and this one belongs
     * to a string somebody typed into a public form. The extra character is the
     * only place in a log line where that difference is visible at a glance, and
     * `newId`'s shape does not care — the suffix is a ULID either way.
     */
    subscription: 'sub',
    /**
     * One tenant intake — the owner's onboarding questionnaire
     * (`docs/tenant-intake-design.md`, migration 0035).
     *
     * `int_`, three characters like `sub_`, and for a related reason: every
     * two-letter prefix here belongs to something inside a tenant's business — a
     * booking, a guest, a table, a photograph — and this belongs to the
     * conversation that happens *before* there is one. An operator reading a
     * support message says "int" and knows they are looking at onboarding rather
     * than at anything the book contains.
     */
    intake: 'int',
    /**
     * One photograph an owner sent from that form (D7).
     *
     * `iup_` and not `pm_`: `photo_album_media` is a family's faces under a code
     * a guest holds, and this is a business owner's picture of their own door,
     * sitting in a different bucket prefix under different grants. The two must
     * never be confusable in a log line, a support ticket or a storage path —
     * which is precisely what a shared prefix would make them — and the object's
     * address ends in this id, so the prefix is literally part of it.
     */
    intakeUpload: 'iup',
    /**
     * One pickup reservation (`docs/lovespa-gaps-plan.md` C1, migration 0039) —
     * a promise about **stock**, as distinct from an order (`or_`), which is a
     * promise about *work*, and from a settlement (`se_`), which is money already
     * recorded.
     *
     * `rv_` and not `or_`, for the reason the migration spends three paragraphs
     * on: these are two tables with two lifecycles, and the one place a shared
     * prefix would hurt most is the place ids are actually read aloud — a support
     * call, a log line, the reference a guest reads off a phone at a counter.
     */
    reservation: 'rv',
    /**
     * One newsletter issue (`docs/lovespa-gaps-plan.md` E3, migration 0042) —
     * the thing the owner writes and sends.
     *
     * `ni_`, two characters, because an issue belongs to the business: it is
     * something she made, like a settlement or an album, rather than something a
     * stranger typed into a form. `sub_` and `int_` are three characters for the
     * opposite reason, and the contrast is the point — an id read aloud in a
     * support call should say which side of that line it is on.
     *
     * A *delivery* gets no prefix at all, because it gets no id: its key is
     * `(tenant, issue, email)`, and that constraint is the idempotency.
     */
    newsletterIssue: 'ni',
    /**
     * One **gift request** (owner's ruling 1, migration 0051) — somebody asking
     * for a gift certificate that the desk then sells and settles.
     *
     * `gr_` and emphatically **not `ct_`**. A certificate is a *liability
     * recognised when it is sold* (§7.4 rule 1: income on `issued_on`), and a
     * request is nobody's money yet — the owner's own rule is that she never
     * wants money she has not earned. Two prefixes because they are two objects
     * with two lifecycles, and because the one place a shared prefix would hurt
     * most is the place ids are read aloud: a support call in which "the ct
     * number" might mean a card worth $100 or an email somebody sent on Tuesday.
     *
     * `rv_`'s argument, with a sharper consequence: a reservation and an order
     * confused each other on a screen, and these two would confuse each other in
     * the ledger.
     */
    giftRequest: 'gr',
    /**
     * **A guest's data request** — export or deletion (spec §7.6, `retention.ts`).
     *
     * Its own prefix because it is its own kind of thing: a task the owner
     * confirms, never a row the guest's session runs. The id is what a guest
     * sees under "Your data" and what `yourstore retention run --request` takes,
     * so it has to be one nobody can mistake for a booking or a gift.
     */
    dataRequest: 'dr',
    /**
     * **A business-level picture uploaded from the operator console** — today the
     * two background photographs of the staff app's look (console redesign B8).
     *
     * No table holds it: the id exists to name the staged and the published
     * object, so an upload is never an overwrite of another (the edge serves a
     * public object for a day). Its own prefix rather than `im_`, because an
     * `im_` is a photograph of something on sale and this is the business's own
     * backdrop — two things a support call should not have to tell apart.
     */
    tenantAsset: 'ta',
};
export function newId(prefix) {
    return `${prefix}_${ulid()}`;
}
const idSchema = (prefix) => z.string().regex(new RegExp(`^${prefix}_[0-9A-HJKMNP-TV-Z]{26}$`), `must be a ${prefix}_ id`);
export const AppointmentId = idSchema(ID_PREFIX.appointment);
export const ClientId = idSchema(ID_PREFIX.client);
export const ServiceId = idSchema(ID_PREFIX.service);
export const ResourceId = idSchema(ID_PREFIX.resource);
/**
 * A staff seat's own id — `staff_users.id`, not the Firebase uid.
 *
 * The uid is the authorization key and is never a parameter anywhere: it arrives
 * inside a verified token and nothing accepts it from a request. This is the
 * row's ULID, and the one thing it is used for is S2's link between a login and
 * the person on the calendar (migration 0044).
 */
export const StaffUserId = idSchema(ID_PREFIX.staff);
export const HoldId = idSchema(ID_PREFIX.hold);
/**
 * One option on one service (`io_…`, `docs/catalog-options-design.md`).
 *
 * It is a parameter — `option_id` rides on an availability ask, a hold and a
 * booking — so it gets a schema like `ServiceId` does, and for the same reason:
 * the shape is checked where it arrives rather than discovered at the join.
 */
export const ItemOptionId = idSchema(ID_PREFIX.itemOption);
export const PaymentLinkId = idSchema(ID_PREFIX.paymentLink);
export const OrderId = idSchema(ID_PREFIX.order);
export const DepartureInstanceId = idSchema(ID_PREFIX.departureInstance);
/**
 * A tenant id is **not** generated. The partner sends `t_lovespa_frenchtown` on
 * every request (agreement section 2), so the operator chooses it at creation
 * and it is stable forever. It is the one id in the system that is human-legible
 * on purpose.
 */
export const TenantId = z
    .string()
    .regex(/^t_[a-z0-9][a-z0-9_]{1,120}$/, 'must be a t_ slug, lower case');
/**
 * `tenants.slug` — a **DNS label**, not a decoration.
 *
 * Spec §6.4.2 gives every tenant `{slug}.book.yourstore.<tld>` under one
 * wildcard certificate, and `app.tenant_for_host()` (migration 0010) resolves
 * that form by stripping the suffix and looking the remaining label up here. A
 * label is what RFC 1123 says it is: lower-case letters, digits and hyphens, no
 * leading or trailing hyphen, at most 63 characters. `tenant_domains` has
 * enforced exactly this shape since 0010 for the hostnames an operator types;
 * the same rule belongs on the slug those hostnames are *derived* from, and
 * since 0016 it is a `CHECK` constraint on `tenants` too.
 */
export const TenantSlug = z
    .string()
    .max(63, 'a DNS label is at most 63 characters')
    .regex(/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/, 'must be a DNS label: a-z, 0-9 and hyphens');
/**
 * The default hostname label for a tenant id — **the one derivation**.
 *
 * A tenant id is `t_demo_spa`; a hostname label cannot carry an underscore, so
 * `demo_spa.book.example.com` is not a name DNS can hold and not a name the
 * wildcard certificate can cover. The default booking site of §6.4.2 was
 * therefore unreachable by construction for every tenant whose id had more than
 * one word — which is all of them. Strip the `t_`, turn underscores into
 * hyphens, and the address exists.
 *
 * This function is the only place that transformation happens. It used to be
 * `id.slice(2)` copied into nine call sites — `createTenant`, the seed roster,
 * three fleet generators and four test fixtures — which is how the underscore
 * survived: nobody owned the rule, so nobody noticed it was wrong.
 *
 * Deliberately **not** a sanitizer. It does not lower-case, strip or truncate,
 * because a tenant id is already constrained to `^t_[a-z0-9][a-z0-9_]{1,120}$`
 * and anything this would have to "fix" is a caller passing something that is
 * not a tenant id. Two ids can only collide here if one already contained a
 * hyphen, which the id constraint forbids — so the mapping is injective over
 * legal ids, and a slug that is not a legal label (a 120-character id) is
 * refused by the constraint rather than silently shortened into someone else's.
 */
export function slugForTenantId(id) {
    return (id.startsWith('t_') ? id.slice('t_'.length) : id).replace(/_/g, '-');
}
/**
 * Ids we accept from the partner and hand back untouched. We store them and
 * never parse them (agreement section 2), so the only rule is a length bound.
 */
export const OpaqueRef = z.string().min(1).max(128);
//# sourceMappingURL=ids.js.map